import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, useQueryParams, useUpdateQueryParam } from '@insurancemenu/imenu-components-lib';
import { ChevronLeft, ChevronRight } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRFQConfigurePlanAttributesStore } from 'store/pages/rfq/rfq-template/configure-plan-attribute/pageStore';
import { PlanAttributeCategory } from 'types/productServiceV2';
import { runInAction, toJS } from 'mobx';
import {
  BACK_TO_PREV_FORM_BTN_TEST_ID,
  BACK_TO_LIST_BTN_TEST_ID,
  SAVE_AND_RETURN_TO_LIST_BTN_TEST_ID,
  SAVE_AND_GO_TO_NEXT_FORM_BTN_TEST_ID,
} from '../../constants';
import { createStyles } from './styles';

export interface FormFooterProps {
  isFormValid: boolean;
  canProceedWithNextForm: boolean;
  canReturnToPreviousForm: boolean;
  returnBack: () => void;
}

const EditAttributeFormFooter = observer(
  ({ isFormValid, canProceedWithNextForm, canReturnToPreviousForm, returnBack }: FormFooterProps): JSX.Element => {
    const query = useQueryParams();
    const updateQueryParam = useUpdateQueryParam();
    const attributeId = query.get('attributeId') || '';
    const attributeCategory: PlanAttributeCategory = query.get('attributeCategory') as PlanAttributeCategory;

    const classes = createStyles();
    // Mobx store
    const { isLoading, getFormByAttributeId, saveRequiredAnswers, saveCustomAnswers, getNewAttribute } =
      useRFQConfigurePlanAttributesStore();

    const saveMethod = attributeCategory === PlanAttributeCategory.REQUIRED ? saveRequiredAnswers : saveCustomAnswers;

    const getFormAndUpdateURLParam = async (
      newAttributeId: string,
      newAttributeCategory: PlanAttributeCategory,
      updateTemplate = false,
    ): Promise<void> => {
      await runInAction(async (): Promise<void> => {
        await getFormByAttributeId(newAttributeId, updateTemplate);
        // Update data in the URL
        updateQueryParam('attributeId', newAttributeId);
        updateQueryParam('attributeCategory', newAttributeCategory || '');
      });
    };

    const saveAndReturnToList = async (): Promise<void> => {
      await saveMethod(attributeId);
      returnBack();
    };

    const backToPreviousForm = async (): Promise<void> => {
      const { attributeId: prevAttributeId, attributeCategory: prevAttributeCategory } = getNewAttribute(
        'PREV',
        attributeId,
      );
      if (prevAttributeId && prevAttributeCategory) {
        await getFormAndUpdateURLParam(prevAttributeId, prevAttributeCategory, true);
      }
    };

    const saveAndGoToNextForm = async (): Promise<void> => {
      const navigationResult = getNewAttribute('NEXT', attributeId);
      const convertedNavigationResult = toJS(navigationResult);
      await saveMethod(attributeId);
      if (convertedNavigationResult.attributeId && convertedNavigationResult.attributeCategory) {
        await getFormAndUpdateURLParam(
          convertedNavigationResult.attributeId,
          convertedNavigationResult.attributeCategory,
          true,
        );
      }
    };

    return (
      <footer className={classes.footer}>
        {canReturnToPreviousForm ? (
          <Button
            type="outlined"
            size="large"
            width="24rem"
            onClick={backToPreviousForm}
            leftIconProp={<ChevronLeft />}
            className={classes.actionButton}
            data-test-id={BACK_TO_PREV_FORM_BTN_TEST_ID}
          >
            Back to Previous Form
          </Button>
        ) : (
          <Button
            type="outlined"
            size="large"
            width="24rem"
            onClick={returnBack}
            leftIconProp={<ChevronLeft />}
            className={classes.actionButton}
            data-test-id={BACK_TO_LIST_BTN_TEST_ID}
          >
            Back to List
          </Button>
        )}
        <div className={classes.continueButtonsWrapper}>
          <Button
            size="large"
            width="24rem"
            color="primary"
            type="outlined"
            onClick={saveAndReturnToList}
            disabled={!isFormValid || isLoading}
            className={classes.actionButton}
            leftIconProp={<ChevronLeft />}
            data-test-id={SAVE_AND_RETURN_TO_LIST_BTN_TEST_ID}
          >
            Save & Return to List
          </Button>
          {canProceedWithNextForm && (
            <Button
              size="large"
              width="24rem"
              color="primary"
              onClick={saveAndGoToNextForm}
              rightIconProp={<ChevronRight />}
              disabled={!isFormValid || isLoading}
              className={classes.actionButton}
              data-test-id={SAVE_AND_GO_TO_NEXT_FORM_BTN_TEST_ID}
            >
              Save & Go to Next Form
            </Button>
          )}
        </div>
      </footer>
    );
  },
);

export default EditAttributeFormFooter;
